import { z } from "zod";
import { stepTypeKey } from "../../drizzle/stepTypes.js";
import { workflowKey } from "../../workflows/workflow-keys.js";
import { costEstimateCheckedDataSchema, costEstimateCheckedDataWithMetadataSchema, costEstimateCommissionAppraisalResultSchema, costEstimateDataSchema, costEstimateDataWithMetadataSchema, costEstimateDocumentsOnlyCheckedDataSchema, costEstimateDocumentsOnlyInputSchema, costEstimateDocumentsOnlyResultSchema, costEstimateRejectResultSchema, costEstimateReleaseResultSchema, } from "../schemas/cost-estimate.js";
import { linkDocumentsResultSchema } from "../schemas/link-documents.js";
/**
 * Cost Estimate Heidelbot Preparation Step (System Step)
 * Fetches cost estimate data from external system (Heidelbot).
 * Run by our API when Heidelbot makes a request to create a cost estimate task.
 */
export const costEstimateHeidelBotPreparationStep = {
    key: stepTypeKey.COST_ESTIMATE_HEIDELBOT_PREPARATION,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: false, // System step — never reached by the banner action
    isSystemStep: true, // Background step - no UI
    resultSchema: costEstimateDataSchema, // Produces full cost estimate data with entities
};
/**
 * Cost Estimate Check Step
 * User reviews extracted cost estimate data alongside the PDF.
 * They can correct AI-extracted values.
 */
export const costEstimateCheckStep = {
    key: stepTypeKey.COST_ESTIMATE_CHECK,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: true, // First user step — form result must be submitted via the in-step button, not the banner
    inputSchema: costEstimateDataWithMetadataSchema, // Receives cost estimate data with previousStepId
    // Produces the full checked data on a decision branch, or the reduced documents-only checked
    // data on the docs-only branch. Full is listed first so a full payload matches it (a reduced
    // payload fails full — missing required cost fields — and falls through to the reduced member).
    resultSchema: z.union([costEstimateCheckedDataSchema, costEstimateDocumentsOnlyCheckedDataSchema]),
    isSystemStep: false,
};
/**
 * Cost Estimate Release Step ("KV Freigeben")
 * Follow-up step after the check step when the reviewer releases the cost estimate
 * for repair. User finishes the workflow in eClaim (REPAIR_SERVICE workbook view).
 */
export const costEstimateReleaseStep = {
    key: stepTypeKey.COST_ESTIMATE_RELEASE,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: false,
    inputSchema: costEstimateCheckedDataWithMetadataSchema,
    resultSchema: costEstimateReleaseResultSchema,
    isSystemStep: false,
};
/**
 * Cost Estimate Commission Appraisal Step ("Gutachten beauftragen")
 * Follow-up step after the check step when the reviewer commissions an expert
 * opinion. User finishes the workflow in eClaim (COMMISSIONED_EXPERT_REPORT workbook view).
 */
export const costEstimateCommissionAppraisalStep = {
    key: stepTypeKey.COST_ESTIMATE_COMMISSION_APPRAISAL,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: false,
    inputSchema: costEstimateCheckedDataWithMetadataSchema,
    resultSchema: costEstimateCommissionAppraisalResultSchema,
    isSystemStep: false,
};
/**
 * Cost Estimate Reject Step ("KV monieren")
 * Follow-up step after the check step when the reviewer rejects the cost estimate
 * for rework. User finishes the workflow in eClaim (ESTIMATE workbook view).
 */
export const costEstimateRejectStep = {
    key: stepTypeKey.COST_ESTIMATE_REJECT,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: false,
    inputSchema: costEstimateCheckedDataWithMetadataSchema,
    resultSchema: costEstimateRejectResultSchema,
    isSystemStep: false,
};
/**
 * Cost Estimate Documents Only Step ("Nur Dokumente verlinken")
 * Docs-only follow-up after the check step: attaches the email's categorized
 * attachments to the existing ContainerEstimate without recording a claim decision
 * or any verified values, and without creating a section. Used e.g. when a later email for
 * an already-recorded incident only carries extra documents.
 */
export const costEstimateDocumentsOnlyStep = {
    key: stepTypeKey.COST_ESTIMATE_DOCUMENTS_ONLY,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: false,
    inputSchema: costEstimateDocumentsOnlyInputSchema,
    resultSchema: costEstimateDocumentsOnlyResultSchema,
    isSystemStep: false,
};
/** What document-manager confirmed linked at the check step's transition. Born SUCCESS, one per task. */
export const costEstimateLinkDocumentsStep = {
    key: stepTypeKey.COST_ESTIMATE_LINK_DOCUMENTS,
    workflow: workflowKey.COST_ESTIMATE,
    blocksBucketCompletion: false,
    resultSchema: linkDocumentsResultSchema,
    isSystemStep: true,
};
/**
 * All cost estimate workflow step definitions
 */
export const costEstimateSteps = {
    [stepTypeKey.COST_ESTIMATE_HEIDELBOT_PREPARATION]: costEstimateHeidelBotPreparationStep,
    [stepTypeKey.COST_ESTIMATE_CHECK]: costEstimateCheckStep,
    [stepTypeKey.COST_ESTIMATE_RELEASE]: costEstimateReleaseStep,
    [stepTypeKey.COST_ESTIMATE_COMMISSION_APPRAISAL]: costEstimateCommissionAppraisalStep,
    [stepTypeKey.COST_ESTIMATE_REJECT]: costEstimateRejectStep,
    [stepTypeKey.COST_ESTIMATE_DOCUMENTS_ONLY]: costEstimateDocumentsOnlyStep,
    [stepTypeKey.COST_ESTIMATE_LINK_DOCUMENTS]: costEstimateLinkDocumentsStep,
};
